
import type { InsertListParams } from '../../api/endpoints';
import type {
  InsertMediaTypeFilter,
  InsertSearchRequest,
  InsertSortKey,
  TagsMode,
} from '../../types/api';

export const PAGE_SIZE = 24;

export const MAX_SHOWCASE_ITEMS = 192;
export const MAX_SEARCH_ITEMS = 96;

export interface LibraryFilters {
  q: string;
  tags: string[];
  tagsMode: TagsMode;
  mood: string[];
  geo: string[];
  mediaType: InsertMediaTypeFilter;
  durationMin: string;
  durationMax: string;
  createdFrom: string;
  createdTo: string;
  sort: InsertSortKey;
}

export const DEFAULT_FILTERS: LibraryFilters = {
  q: '',
  tags: [],
  tagsMode: 'all',
  mood: [],
  geo: [],
  mediaType: 'any',
  durationMin: '',
  durationMax: '',
  createdFrom: '',
  createdTo: '',
  sort: 'relevance',
};

export const SORT_LABELS: Readonly<Record<InsertSortKey, string>> = {
  relevance: 'По релевантности',
  '-created_at': 'Сначала новые',
  created_at: 'Сначала старые',
  '-duration': 'Сначала длинные',
  duration: 'Сначала короткие',
  title: 'По названию',
};

export const SORT_ORDER: readonly InsertSortKey[] = [
  'relevance',
  '-created_at',
  'created_at',
  '-duration',
  'duration',
  'title',
];

export function hasQuery(filters: LibraryFilters): boolean {
  return filters.q.trim().length > 0;
}

export function effectiveSort(filters: LibraryFilters): InsertSortKey {
  if (filters.sort === 'relevance' && !hasQuery(filters)) return '-created_at';
  return filters.sort;
}

export function isSemantic(filters: LibraryFilters): boolean {
  return effectiveSort(filters) === 'relevance';
}

function seconds(raw: string): number | undefined {
  const value = Number.parseFloat(raw.replace(',', '.'));
  if (!Number.isFinite(value) || value < 0) return undefined;
  return Math.round(value * 1000);
}

function dayStart(raw: string): string | undefined {
  return raw ? `${raw}T00:00:00` : undefined;
}
function dayEnd(raw: string): string | undefined {
  return raw ? `${raw}T23:59:59` : undefined;
}

export function filterProblems(filters: LibraryFilters): string[] {
  const problems: string[] = [];
  const min = seconds(filters.durationMin);
  const max = seconds(filters.durationMax);
  if (min !== undefined && max !== undefined && min > max) {
    problems.push('Длительность «от» больше, чем «до»');
  }
  if (filters.createdFrom && filters.createdTo && filters.createdFrom > filters.createdTo) {
    problems.push('Дата «с» позже, чем «по»');
  }
  return problems;
}

export function toListParams(filters: LibraryFilters, limit: number): InsertListParams {
  return {
    q: filters.q.trim() || undefined,
    tags: filters.tags.length ? filters.tags : undefined,
    tags_mode: filters.tags.length ? filters.tagsMode : undefined,
    mood: filters.mood.length ? filters.mood : undefined,
    geo: filters.geo.length ? filters.geo : undefined,
    media_type: filters.mediaType === 'any' ? undefined : filters.mediaType,
    duration_min_ms: seconds(filters.durationMin),
    duration_max_ms: seconds(filters.durationMax),
    created_from: dayStart(filters.createdFrom),
    created_to: dayEnd(filters.createdTo),
    sort: effectiveSort(filters),
    with_media: true,
    limit,
    offset: 0,
  };
}

export function toSearchRequest(filters: LibraryFilters, limit: number): InsertSearchRequest {
  return {
    q: filters.q.trim(),
    tags: filters.tags.length ? filters.tags : undefined,
    tags_mode: filters.tags.length ? filters.tagsMode : undefined,
    mood: filters.mood.length ? filters.mood : undefined,
    geo: filters.geo.length ? filters.geo : undefined,
    media_type: filters.mediaType === 'any' ? undefined : filters.mediaType,
    duration_min_ms: seconds(filters.durationMin) ?? null,
    duration_max_ms: seconds(filters.durationMax) ?? null,
    created_from: dayStart(filters.createdFrom) ?? null,
    created_to: dayEnd(filters.createdTo) ?? null,
    limit,
    offset: 0,
  };
}

export type FilterSection = 'tags' | 'mood' | 'geo' | 'mediaType' | 'duration' | 'dates';

export interface FilterChip {
  section: FilterSection;
  label: string;
  value: string;
  note?: string;
  active: boolean;
}

const MEDIA_LABELS: Readonly<Record<InsertMediaTypeFilter, string>> = {
  any: 'всё',
  video: 'видео',
  photo: 'фото',
};

function listValue(values: readonly string[], fallback: string): string {
  if (values.length === 0) return fallback;
  if (values.length <= 3) return values.join(', ');
  return `${values.slice(0, 2).join(', ')} и ещё ${values.length - 2}`;
}

function durationValue(filters: LibraryFilters): string {
  const { durationMin: min, durationMax: max } = filters;
  if (!min && !max) return 'любая';
  if (min && max) return `${min}–${max} с`;
  if (min) return `от ${min} с`;
  return `до ${max} с`;
}

function datesValue(filters: LibraryFilters): string {
  const { createdFrom: from, createdTo: to } = filters;
  if (!from && !to) return 'всё время';
  if (from && to) return `${from} – ${to}`;
  return from ? `с ${from}` : `по ${to}`;
}

export function buildChips(filters: LibraryFilters): FilterChip[] {
  return [
    {
      section: 'tags',
      label: 'Теги',
      value: listValue(filters.tags, 'не выбраны'),
      note: filters.tags.length > 1 ? (filters.tagsMode === 'all' ? 'все сразу' : 'любой') : '',
      active: filters.tags.length > 0,
    },
    {
      section: 'mood',
      label: 'Настроение',
      value: listValue(filters.mood, 'любое'),
      active: filters.mood.length > 0,
    },
    {
      section: 'geo',
      label: 'Гео',
      value: listValue(filters.geo, 'любое'),
      active: filters.geo.length > 0,
    },
    {
      section: 'mediaType',
      label: 'Тип',
      value: MEDIA_LABELS[filters.mediaType],
      active: filters.mediaType !== 'any',
    },
    {
      section: 'duration',
      label: 'Длительность',
      value: durationValue(filters),
      active: Boolean(filters.durationMin || filters.durationMax),
    },
    {
      section: 'dates',
      label: 'Даты',
      value: datesValue(filters),
      active: Boolean(filters.createdFrom || filters.createdTo),
    },
  ];
}

export function activeFilterCount(filters: LibraryFilters): number {
  return buildChips(filters).filter((chip) => chip.active).length;
}

export function isPristine(filters: LibraryFilters): boolean {
  return !hasQuery(filters) && activeFilterCount(filters) === 0;
}

export function describeActiveFilters(filters: LibraryFilters): string {
  return buildChips(filters)
    .filter((chip) => chip.active)
    .map((chip) => `${chip.label.toLowerCase()}: ${chip.value}`)
    .join('; ');
}
